import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes, css } from 'styled-components';
import ReactTooltip from 'react-tooltip';
import Tooltip from '../Tooltip';
import Icon from '../Icon';
import Button from '../Button';
import Image from '../Image';
import theme, { rem } from '../../theme';

const showLine = keyframes`
  0%{
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const MixinShowLine = css`
  animation: ${showLine} 0.35s ease;
  animation-delay: 0.4s;
  animation-fill-mode: forwards;
`;

const StyledImage = styled(Image)`
  position: absolute;
  right: ${rem(2)};
  top: -${rem(4)};
`;

const StyledNav = styled.div`
  display: flex;
  position: ${props => (props.fixed ? 'fixed' : 'relative')};
  left: ${props => (props.fixed ? rem(5) : 'auto')};
  top: ${props => (props.fixed ? rem(100) : 'auto')};
  flex-direction: column;
  align-items: center;
  min-width: calc(${rem(70)} - ${theme.spacing.default}*2);
  height: auto;
`;

const StyledItem = styled(Button)`
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props =>
    props.fixed ? 'auto' : `calc(100% + ${theme.spacing.default}*2)`};
  height: ${rem(45)};
  cursor: pointer;
  border-radius: ${props => (props.fixed ? '50%' : '')};
  padding: ${props => (props.fixed ? rem(10) : 0)};
  margin-bottom: ${props => (props.fixed ? rem(3) : 0)};
  background: ${props => (props.fixed ? theme.color.dark[100] : '')};
  transform: ${props => (props.fixed ? 'scale(1)' : 'scale(1)')};
  transition: 0.4s;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    width: 0.4rem;
    height: 100%;
    z-index: -1;
    opacity: 0;
  }

  &:hover,
  &:focus {
    i {
      color: ${props =>
        props.indicator && !props.fixed
          ? theme.color.indicator[props.indicator]
          : '#fff'};
    }
  }

  &.active {
    background: ${props => (props.fixed ? theme.color.primary[100] : '')};
    transform: scale(1);
    .show & {
      &:before {
        border-left: 0.3rem solid
          ${props => theme.color.indicator[props.indicator]};
        ${MixinShowLine}
      }
    }
  }
`;

/**
 * Return a styled component
 * @param {object} children
 * @param {bool} fixed
 * @param {string} itemId
 */
const ScrollSpyNav = props => {
  const { itemId } = props;

  //smoothscroll.js file
  /*
   * smoothscroll polyfill - v0.3.5
   * https://iamdustan.github.io/smoothscroll
   * 2016 (c) Dustan Kasten, Jeremias Menichelli - MIT License
   */

  (function(w, d, undefined) {
    'use strict';

    /*
     * aliases
     * w: window global object
     * d: document
     * undefined: undefined
     */

    /**
     * polyfill
     */
    function polyfill() {
      // return when scrollBehavior interface is supported
      if ('scrollBehavior' in d.documentElement.style) {
        return;
      }

      /*
       * globals
       */
      var Element = w.HTMLElement || w.Element;
      var SCROLL_TIME = 468;

      /*
       * object gathering original scroll methods
       */
      var original = {
        scroll: w.scroll || w.scrollTo,
        scrollBy: w.scrollBy,
        elScroll: Element.prototype.scroll || scrollElement,
        scrollIntoView: Element.prototype.scrollIntoView,
      };

      /*
       * define timing method
       */
      var now =
        w.performance && w.performance.now
          ? w.performance.now.bind(w.performance)
          : Date.now;

      /**
       * changes scroll position inside an element
       * @method scrollElement
       * @param {Number} x
       * @param {Number} y
       */
      function scrollElement(x, y) {
        this.scrollLeft = x;
        this.scrollTop = y;
      }

      /**
       * returns result of applying ease math function to a number
       * @method ease
       * @param {Number} k
       * @returns {Number}
       */
      function ease(k) {
        return 0.5 * (1 - Math.cos(Math.PI * k));
      }

      /**
       * indicates if a smooth behavior should be applied
       * @method shouldBailOut
       * @param {Number|Object} x
       * @returns {Boolean}
       */
      function shouldBailOut(x) {
        if (
          typeof x !== 'object' ||
          x === null ||
          x.behavior === undefined ||
          x.behavior === 'auto' ||
          x.behavior === 'instant'
        ) {
          // first arg not an object/null
          // or behavior is auto, instant or undefined
          return true;
        }

        if (typeof x === 'object' && x.behavior === 'smooth') {
          // first argument is an object and behavior is smooth
          return false;
        }

        // throw error when behavior is not supported
        throw new TypeError('behavior not valid');
      }

      /**
       * finds scrollable parent of an element
       * @method findScrollableParent
       * @param {Node} el
       * @returns {Node} el
       */
      function findScrollableParent(el) {
        var isBody;
        var hasScrollableSpace;
        var hasVisibleOverflow;

        do {
          el = el.parentNode;

          // set condition variables
          isBody = el === d.body;
          hasScrollableSpace =
            el.clientHeight < el.scrollHeight ||
            el.clientWidth < el.scrollWidth;
          hasVisibleOverflow =
            w.getComputedStyle(el, null).overflow === 'visible';
        } while (!isBody && !(hasScrollableSpace && !hasVisibleOverflow));

        isBody = hasScrollableSpace = hasVisibleOverflow = null;

        return el;
      }

      /**
       * self invoked function that, given a context, steps through scrolling
       * @method step
       * @param {Object} context
       */
      function step(context) {
        var time = now();
        var value;
        var currentX;
        var currentY;
        var elapsed = (time - context.startTime) / SCROLL_TIME;

        // avoid elapsed times higher than one
        elapsed = elapsed > 1 ? 1 : elapsed;

        // apply easing to elapsed time
        value = ease(elapsed);

        currentX = context.startX + (context.x - context.startX) * value;
        currentY = context.startY + (context.y - context.startY) * value;

        context.method.call(context.scrollable, currentX, currentY);

        // scroll more if we have not reached our destination
        if (currentX !== context.x || currentY !== context.y) {
          w.requestAnimationFrame(step.bind(w, context));
        }
      }

      /**
       * scrolls window with a smooth behavior
       * @method smoothScroll
       * @param {Object|Node} el
       * @param {Number} x
       * @param {Number} y
       */
      function smoothScroll(el, x, y) {
        var scrollable;
        var startX;
        var startY;
        var method;
        var startTime = now();

        // define scroll context
        if (el === d.body) {
          scrollable = w;
          startX = w.scrollX || w.pageXOffset;
          startY = w.scrollY || w.pageYOffset;
          method = original.scroll;
        } else {
          scrollable = el;
          startX = el.scrollLeft;
          startY = el.scrollTop;
          method = scrollElement;
        }

        // scroll looping over a frame
        step({
          scrollable: scrollable,
          method: method,
          startTime: startTime,
          startX: startX,
          startY: startY,
          x: x,
          y: y,
        });
      }

      /*
       * ORIGINAL METHODS OVERRIDES
       */

      // w.scroll and w.scrollTo
      w.scroll = w.scrollTo = function() {
        // avoid smooth behavior if not required
        if (shouldBailOut(arguments[0])) {
          original.scroll.call(
            w,
            arguments[0].left || arguments[0],
            arguments[0].top || arguments[1]
          );
          return;
        }

        // LET THE SMOOTHNESS BEGIN!
        smoothScroll.call(w, d.body, ~~arguments[0].left, ~~arguments[0].top);
      };

      // w.scrollBy
      w.scrollBy = function() {
        // avoid smooth behavior if not required
        if (shouldBailOut(arguments[0])) {
          original.scrollBy.call(
            w,
            arguments[0].left || arguments[0],
            arguments[0].top || arguments[1]
          );
          return;
        }

        // LET THE SMOOTHNESS BEGIN!
        smoothScroll.call(
          w,
          d.body,
          ~~arguments[0].left + (w.scrollX || w.pageXOffset),
          ~~arguments[0].top + (w.scrollY || w.pageYOffset)
        );
      };

      // Element.prototype.scroll and Element.prototype.scrollTo
      Element.prototype.scroll = Element.prototype.scrollTo = function() {
        // avoid smooth behavior if not required
        if (shouldBailOut(arguments[0])) {
          original.elScroll.call(
            this,
            arguments[0].left || arguments[0],
            arguments[0].top || arguments[1]
          );
          return;
        }

        // LET THE SMOOTHNESS BEGIN!
        smoothScroll.call(this, this, arguments[0].left, arguments[0].top);
      };

      // Element.prototype.scrollBy
      Element.prototype.scrollBy = function() {
        var arg0 = arguments[0];

        if (typeof arg0 === 'object') {
          this.scroll({
            left: arg0.left + this.scrollLeft,
            top: arg0.top + this.scrollTop,
            behavior: arg0.behavior,
          });
        } else {
          this.scroll(this.scrollLeft + arg0, this.scrollTop + arguments[1]);
        }
      };

      // Element.prototype.scrollIntoView
      Element.prototype.scrollIntoView = function() {
        // avoid smooth behavior if not required
        if (shouldBailOut(arguments[0])) {
          original.scrollIntoView.call(this, arguments[0] || true);
          return;
        }

        // LET THE SMOOTHNESS BEGIN!
        var scrollableParent = findScrollableParent(this);
        var parentRects = scrollableParent.getBoundingClientRect();
        var clientRects = this.getBoundingClientRect();

        if (scrollableParent !== d.body) {
          // reveal element inside parent
          smoothScroll.call(
            this,
            scrollableParent,
            scrollableParent.scrollLeft + clientRects.left - parentRects.left,
            scrollableParent.scrollTop + clientRects.top - parentRects.top
          );
          // reveal parent in viewport
          w.scrollBy({
            left: parentRects.left,
            top: parentRects.top,
            behavior: 'smooth',
          });
        } else {
          // reveal element in viewport
          w.scrollBy({
            left: clientRects.left,
            top: clientRects.top,
            behavior: 'smooth',
          });
        }
      };
    }

    if (typeof exports === 'object') {
      // commonjs
      module.exports = { polyfill: polyfill };
    } else {
      // global
      polyfill();
    }
  })(window, document);

  /** Onclick handler */
  const onClick = (e, myRef) => {
    //Check if there is a navigational prop attached
    //let children = document.getElementById('js-SpyNav' + itemId).children;
    // if (!myRef.current.classList.contains('active')) {
    //   for (let i = 0; i < children.length; i++) {
    //     children[i].classList.remove('active');
    //   }
    // }
    if (
      document.querySelectorAll(
        '[data-scrollid=' + e.currentTarget.dataset.scrollto + ']'
      )[0] ||
      document.getElementById('js-SpyNav' + itemId).contains(e.currentTarget)
    ) {
      let attr = e.currentTarget.dataset.scrollto;
      let lastItem = false;
      const total =
        document.getElementById('js-SpyNav' + itemId).childElementCount - 1;
      const position = e.currentTarget.dataset.position;

      if (
        document.getElementById('js-SpyNav' + itemId).contains(e.currentTarget)
      ) {
        attr = e.currentTarget.closest('[data-scrollto]').dataset.scrollto;
      }
      if (document.querySelectorAll('[data-scrollid=' + attr + ']')[0]) {
        if (position.toString() === total.toString()) {
          //get Scrollbar position
          lastItem = true;
        }
        const obj = document.querySelectorAll(
          '[data-scrollid=' + attr + ']'
        )[0];
        if (obj.offsetTop < obj.parentElement.clientHeight / 2) {
          lastItem = true;
        }

        document
          .querySelectorAll('[data-scrollid=' + attr + ']')[0]
          .scrollIntoView({
            behavior: 'smooth',
            block: lastItem ? 'end' : 'start',
          });

        //scroll into view
        document
          .querySelectorAll('[data-scrollid=' + attr + ']')[0]
          .scrollIntoView({
            behavior: 'smooth',
            block: lastItem ? 'end' : 'start',
          });
      }
    }
  };

  /**
   * Return a styled component
   */
  const componentCheck = () => {
    const {
      icons,
      iconColor,
      indicators,
      scrollTos,
      fixed,
      itemId,
      badges,
      toolTips,
    } = props;

    let iconsArray = icons.split(' ');
    let badgesArray = badges && badges.split(' ');
    let scrollToArray = scrollTos.split(' ');
    let indicatorArray = indicators.split(' ');
    let tooltipArray = toolTips ? toolTips.split(' ') : [];

    const nodes = React.Children.map(scrollToArray, (child, i) => {
      const myRef = useRef();
      let element = (
        <StyledItem
          fixed={fixed}
          data-for={'innertip' + itemId + i}
          data-tip=''
          data-scrollto={scrollToArray[i]}
          data-position={i}
          indicator={indicatorArray[i]}
          onClick={e => onClick(e, myRef)}
          ref={myRef}
        >
          <Tooltip
            id={'innertip' + itemId + i}
            delayShow={3}
            content={{
              title: {
                text: tooltipArray[i],
                textStyle: 'body',
                textColor: '#fff',
                align: 'center',
              },
            }}
            noSpacing
          />
          <Icon size={24} color={iconColor} icon={iconsArray[i]} />
          {badgesArray && badgesArray[i] !== 'empty' && (
            <StyledImage
              src={'/resources/' + badgesArray[i] + '.svg'}
              width={rem(15)}
            />
          )}
        </StyledItem>
      );

      return element;
    });

    let result = (
      <StyledNav id={'js-SpyNav' + itemId} fixed={fixed} {...props}>
        {nodes}
      </StyledNav>
    );

    return result;
  };

  /**
   * Returns styled list
   */
  return componentCheck();
};

ScrollSpyNav.propTypes = {
  /** Can have all kind of child nodes */
  children: PropTypes.node,
  fixed: PropTypes.bool,
  itemId: PropTypes.string,
};

ScrollSpyNav.defaultProps = {
  children: null,
  fixed: false,
  itemId: null,
};

/** @component */
export default ScrollSpyNav;
